import React, { useState } from 'react';
import Markdown from 'react-markdown';
import { styled } from 'styled-components';
import type { Trace, TraceEvent } from '@dazzle/api-client';

import { Button } from 'Compass/components/styled/Button';
import { formatAsYaml, isJsonString } from 'Compass/utils/yamlFormatting';

interface TraceDetailModalProps {
  trace: Trace;
  onClose: () => void;
}

const getTraceIdentifier = (trace: Trace): string =>
  trace.message_id ?? trace.trace_id ?? 'unknown';

const downloadTraceJson = (trace: Trace) => {
  const blob = new Blob([JSON.stringify(trace, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `trace-${getTraceIdentifier(trace)}.json`;
  a.click();
  URL.revokeObjectURL(url);
};

const TRACE_STATUS_LABELS: Record<number, string> = {
  1: 'Pending',
  2: 'Completed',
  3: 'Error'
};

const TRACE_EVENT_TYPE_LABELS: Record<number, string> = {
  1: 'LLM',
  2: 'LLM',
  3: 'LLM Error',
  4: 'Tool',
  5: 'Tool',
  6: 'Tool Error',
  7: 'Node',
  8: 'Node',
  9: 'Node Error'
};

const formatDuration = (ms: number | null | undefined): string => {
  if (ms === null || ms === undefined) return 'N/A';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

const formatTokens = (tokens: number | null | undefined): string => {
  if (tokens === null || tokens === undefined) return 'N/A';
  return tokens.toLocaleString();
};

const PROMPT_TYPE_LABELS: Record<string, string> = {
  'system': 'SystemMessage',
  'human': 'HumanMessage',
  'user': 'UserMessage',
  'ai': 'AIMessage',
  'model': 'ModelMessage',
  'tool': 'ToolMessage',
  'function': 'FunctionMessage',
};

const getPromptMessageType = (prompt: Record<string, unknown>): string => {
  const role = prompt.role as string | undefined;
  if (role) {
    return PROMPT_TYPE_LABELS[role] || role;
  }
  const kwargs = prompt.kwargs as Record<string, unknown> | undefined;
  const type = kwargs?.type as string | undefined;
  if (type) {
    return PROMPT_TYPE_LABELS[type] || type;
  }
  return 'Message';
};

const getPromptContent = (prompt: Record<string, unknown>): unknown => {
  if (prompt.content !== undefined) return prompt.content;
  const kwargs = prompt.kwargs as Record<string, unknown> | undefined;
  if (kwargs?.content !== undefined) return kwargs.content;
  return prompt;
};

const TraceDetailModal = ({ trace, onClose }: TraceDetailModalProps) => {
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const [expandedPrompts, setExpandedPrompts] = useState<Set<string>>(new Set());
  const [finalResponseExpanded, setFinalResponseExpanded] = useState(false);

  const toggleEvent = (eventId: string) => {
    setExpandedEvents(prev => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };

  const togglePrompt = (promptKey: string) => {
    setExpandedPrompts(prev => {
      const next = new Set(prev);
      if (next.has(promptKey)) {
        next.delete(promptKey);
      } else {
        next.add(promptKey);
      }
      return next;
    });
  };

  const getStatusColor = (status: Trace['status']): string => {
    switch (status) {
      case 1: return '#ed8936';
      case 2: return '#38a169';
      case 3: return '#e53e3e';
      default: return '#a0aec0';
    }
  };

  const getEventTypeColor = (eventType: TraceEvent['event_type']): string => {
    switch (eventType) {
      case 1:
      case 2: return '#008AFF';
      case 3: return '#e53e3e';
      case 4:
      case 5: return '#743D98';
      case 6: return '#e53e3e';
      case 7:
      case 8: return '#38a169';
      case 9: return '#e53e3e';
      default: return '#a0aec0';
    }
  };

  const llmEndEvents = trace.events.filter((e: TraceEvent) => e.event_type === 2);
  const toolEndEvents = trace.events.filter((e: TraceEvent) => e.event_type === 5);
  const nodeEndEvents = trace.events.filter((e: TraceEvent) => e.event_type === 8);
  const hasRetries = trace.events.some((e: TraceEvent) => e.retry_count !== undefined && e.retry_count > 0);
  const wasRateLimited = trace.events.some((e: TraceEvent) => e.rate_limited === true);
  const firstLlmEventId = llmEndEvents[0]?.event_id;

  return (
    <ModalContent>
      <ModalHeader>
        <HeaderInfo>
          <HeaderTitle>Trace Details</HeaderTitle>
          <MessageId>
            {trace.message_id ? `Message: ${trace.message_id}` : `Trace: ${trace.trace_id}`}
          </MessageId>
        </HeaderInfo>
        <HeaderBadges>
          <DownloadButton onClick={() => downloadTraceJson(trace)} title="Download raw JSON">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 1v9m0 0L5 7m3 3l3-3M2 12v1a2 2 0 002 2h8a2 2 0 002-2v-1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </DownloadButton>
          <StatusBadge $color={getStatusColor(trace.status)}>
            {TRACE_STATUS_LABELS[trace.status] || 'Unknown'}
          </StatusBadge>
        </HeaderBadges>
      </ModalHeader>

      <SummarySection>
        <SummaryGrid>
          <SummaryItem>
            <SummaryLabel>Model</SummaryLabel>
            <SummaryValue>{trace.model_name || 'N/A'}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Total Duration</SummaryLabel>
            <SummaryValue>{formatDuration(trace.total_duration_ms)}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Total Tokens</SummaryLabel>
            <SummaryValue>{formatTokens(trace.total_token_usage?.total_tokens)}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>LLM Calls</SummaryLabel>
            <SummaryValue>{llmEndEvents.length}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Tool Calls</SummaryLabel>
            <SummaryValue>{toolEndEvents.length}</SummaryValue>
          </SummaryItem>
          {nodeEndEvents.length > 0 && (
            <SummaryItem>
              <SummaryLabel>Graph Nodes</SummaryLabel>
              <SummaryValue>{nodeEndEvents.length}</SummaryValue>
            </SummaryItem>
          )}
          <SummaryItem>
            <SummaryLabel>Started</SummaryLabel>
            <SummaryValue>{new Date(trace.start_ts).toLocaleString()}</SummaryValue>
          </SummaryItem>
          {(hasRetries || wasRateLimited) && (
            <SummaryItem>
              <SummaryLabel>Issues</SummaryLabel>
              <SummaryValue>
                {hasRetries && <SummaryRetryBadge>Has Retries</SummaryRetryBadge>}
                {wasRateLimited && <SummaryRateLimitBadge>Rate Limited</SummaryRateLimitBadge>}
              </SummaryValue>
            </SummaryItem>
          )}
        </SummaryGrid>
      </SummarySection>

      {trace.error_message && (
        <TopLevelErrorSection>
          <TopLevelErrorLabel>Unhandled Error</TopLevelErrorLabel>
          <TopLevelErrorMessage>{trace.error_message}</TopLevelErrorMessage>
        </TopLevelErrorSection>
      )}

      {trace.final_response && (
        <FinalResponseSection>
          <FinalResponseHeader onClick={() => setFinalResponseExpanded(prev => !prev)}>
            <FinalResponseLabel>Final Response</FinalResponseLabel>
            <ExpandIcon>{finalResponseExpanded ? '▼' : '▶'}</ExpandIcon>
          </FinalResponseHeader>
          {finalResponseExpanded && (
            <FinalResponseContent>
              {isJsonString(trace.final_response) ? (
                <pre>{formatAsYaml(trace.final_response)}</pre>
              ) : (
                <Markdown>{trace.final_response}</Markdown>
              )}
            </FinalResponseContent>
          )}
        </FinalResponseSection>
      )}

      <EventsSection>
        <EventsSectionTitle>Event Timeline</EventsSectionTitle>
        <EventsList>
          {trace.events.map((event) => (
            <EventItem key={event.event_id}>
              <EventHeader onClick={() => toggleEvent(event.event_id)}>
                <EventTypeIndicator>
                  <EventTypeBadge $color={getEventTypeColor(event.event_type)}>
                    {TRACE_EVENT_TYPE_LABELS[event.event_type] || 'Unknown'}
                  </EventTypeBadge>
                  {event.llm_data && (
                    <EventModelName>{event.llm_data.model_name}</EventModelName>
                  )}
                  {event.llm_data?.finish_reason && event.llm_data.finish_reason !== 'FinishReason.STOP' && (
                    <FinishReasonBadge>{event.llm_data.finish_reason.replace('FinishReason.', '')}</FinishReasonBadge>
                  )}
                  {event.tool_data && (
                    <EventToolName>{event.tool_data.tool_name}</EventToolName>
                  )}
                  {event.node_data && (
                    <EventNodeName>{event.node_data.node_name}</EventNodeName>
                  )}
                  {event.retry_count !== undefined && event.retry_count > 0 && (
                    <RetryBadge>↻ {event.retry_count} retries</RetryBadge>
                  )}
                  {event.rate_limited && (
                    <RateLimitBadge>Rate Limited</RateLimitBadge>
                  )}
                </EventTypeIndicator>
                <EventMeta>
                  {event.llm_data?.token_usage && (
                    <EventTokens>{formatTokens(event.llm_data.token_usage.total_tokens)} tokens</EventTokens>
                  )}
                  {event.duration_ms !== null && event.duration_ms !== undefined && (
                    <EventDuration>{formatDuration(event.duration_ms)}</EventDuration>
                  )}
                  <ExpandIcon>{expandedEvents.has(event.event_id) ? '▼' : '▶'}</ExpandIcon>
                </EventMeta>
              </EventHeader>

              {expandedEvents.has(event.event_id) && (
                <EventDetails>
                  {event.error_message && (
                    <ErrorSection>
                      <ErrorLabel>Error</ErrorLabel>
                      <ErrorMessage>{event.error_message}</ErrorMessage>
                    </ErrorSection>
                  )}

                  {event.event_id === firstLlmEventId && event.llm_data?.prompts && event.llm_data.prompts.length > 0 && (
                    <PromptsSection>
                      <PromptsHeader>Prompts</PromptsHeader>
                      <PromptsContent>
                        {event.llm_data.prompts.map((prompt: Record<string, unknown>, idx: number) => {
                          const promptKey = `${event.event_id}-${idx}`;
                          const isPromptExpanded = expandedPrompts.has(promptKey);
                          return (
                            <PromptItem key={idx}>
                              <PromptHeader onClick={() => togglePrompt(promptKey)}>
                                <PromptTypeLabel>{getPromptMessageType(prompt)}</PromptTypeLabel>
                                <ExpandIcon>{isPromptExpanded ? '▼' : '▶'}</ExpandIcon>
                              </PromptHeader>
                              {isPromptExpanded && (
                                <PromptContent>
                                  <pre>{formatAsYaml(getPromptContent(prompt))}</pre>
                                </PromptContent>
                              )}
                            </PromptItem>
                          );
                        })}
                      </PromptsContent>
                    </PromptsSection>
                  )}

                  {event.llm_data?.reasoning && (
                    <ReasoningSection>
                      <ReasoningHeader>Model Reasoning</ReasoningHeader>
                      <ReasoningContent>
                        <Markdown>{event.llm_data.reasoning}</Markdown>
                      </ReasoningContent>
                    </ReasoningSection>
                  )}

                  {event.llm_data?.completion && (
                    <CompletionSection>
                      <CompletionHeader>Completion</CompletionHeader>
                      <CompletionContent>
                        {isJsonString(event.llm_data.completion) ? (
                          <pre>{formatAsYaml(event.llm_data.completion)}</pre>
                        ) : (
                          <Markdown>{event.llm_data.completion}</Markdown>
                        )}
                      </CompletionContent>
                    </CompletionSection>
                  )}

                  {event.llm_data?.token_usage && (
                    <TokenSection>
                      <TokenHeader>Token Usage</TokenHeader>
                      <TokenGrid>
                        <TokenItem>
                          <TokenLabel>Prompt</TokenLabel>
                          <TokenValue>{formatTokens(event.llm_data.token_usage.prompt_tokens)}</TokenValue>
                        </TokenItem>
                        <TokenItem>
                          <TokenLabel>Completion</TokenLabel>
                          <TokenValue>{formatTokens(event.llm_data.token_usage.completion_tokens)}</TokenValue>
                        </TokenItem>
                        <TokenItem>
                          <TokenLabel>Total</TokenLabel>
                          <TokenValue>{formatTokens(event.llm_data.token_usage.total_tokens)}</TokenValue>
                        </TokenItem>
                      </TokenGrid>
                    </TokenSection>
                  )}

                  {event.tool_data && (
                    <ToolSection>
                      <ToolHeader>Tool: {event.tool_data.tool_name}</ToolHeader>
                      <ToolInputOutput>
                        <ToolBlock>
                          <ToolBlockLabel>Input Arguments</ToolBlockLabel>
                          <ToolBlockContent>
                            <pre>{formatAsYaml(event.tool_data.input_args)}</pre>
                          </ToolBlockContent>
                        </ToolBlock>
                        {event.tool_data.output !== null && event.tool_data.output !== undefined && (
                          <ToolBlock>
                            <ToolBlockLabel>Output</ToolBlockLabel>
                            <ToolBlockContent>
                              <pre>{formatAsYaml(event.tool_data.output)}</pre>
                            </ToolBlockContent>
                          </ToolBlock>
                        )}
                      </ToolInputOutput>
                    </ToolSection>
                  )}

                  {event.node_data && (
                    <NodeSection>
                      <NodeHeader>Node: {event.node_data.node_name}</NodeHeader>
                      {event.node_data.node_type && (
                        <NodeType>Type: {event.node_data.node_type}</NodeType>
                      )}
                    </NodeSection>
                  )}
                </EventDetails>
              )}
            </EventItem>
          ))}
        </EventsList>
      </EventsSection>

      <ModalFooter>
        <Button onClick={onClose}>Close</Button>
      </ModalFooter>
    </ModalContent>
  );
};

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  width: 900px;
  max-width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 24px 32px;
  background: linear-gradient(to bottom, #FAFAFC, #ffffff);
  border-bottom: 1px solid #E4E7EF;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const HeaderTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1A1D2B;
  margin: 0;
`;

const MessageId = styled.div`
  font-size: 12px;
  color: #8890A5;
  font-family: monospace;
`;

const HeaderBadges = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DownloadButton = styled.button`
  background: none;
  border: 1px solid #E4E7EF;
  border-radius: 6px;
  padding: 6px;
  cursor: pointer;
  color: #8890A5;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #F0F1F6;
    color: #5A6178;
  }
`;

const StatusBadge = styled.span<{ $color: string }>`
  background: ${props => props.$color};
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
`;

const SummarySection = styled.div`
  padding: 20px 32px;
  background: #F8F9FC;
  border-bottom: 1px solid #E4E7EF;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #8890A5;
  text-transform: uppercase;
`;

const SummaryValue = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #1A1D2B;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const SummaryRetryBadge = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #c05621;
  background: #feebc8;
  padding: 2px 8px;
  border-radius: 4px;
`;

const SummaryRateLimitBadge = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #c53030;
  background: #fed7d7;
  padding: 2px 8px;
  border-radius: 4px;
`;

const PromptsSection = styled.div`
  background: #f0f9ff;
  border: 1px solid #90cdf4;
  border-radius: 6px;
  overflow: hidden;
`;

const PromptsHeader = styled.div`
  padding: 8px 12px;
  background: #bee3f8;
  font-size: 12px;
  font-weight: 600;
  color: #2c5282;
  text-transform: uppercase;
`;

const PromptsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
`;

const PromptItem = styled.div`
  background: white;
  border: 1px solid #E4E7EF;
  border-radius: 6px;
  overflow: hidden;
`;

const PromptHeader = styled.div`
  padding: 8px 12px;
  background: #F0F1F6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: #E4E7EF;
  }
`;

const PromptTypeLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #5A6178;
`;

const PromptContent = styled.div`
  padding: 12px;
  max-height: 300px;
  overflow-y: auto;

  pre {
    margin: 0;
    font-size: 12px;
    font-family: monospace;
    white-space: pre-wrap;
    word-break: break-word;
    color: #1A1D2B;
  }
`;

const EventsSection = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 32px;
`;

const EventsSectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #5A6178;
  margin: 0 0 16px 0;
`;

const EventsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const EventItem = styled.div`
  border: 1px solid #E4E7EF;
  border-radius: 8px;
  overflow: hidden;
`;

const EventHeader = styled.div`
  padding: 12px 16px;
  background: #F8F9FC;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: #F0F1F6;
  }
`;

const EventTypeIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const EventTypeBadge = styled.span<{ $color: string }>`
  background: ${props => props.$color};
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
`;

const EventModelName = styled.span`
  font-size: 13px;
  color: #5A6178;
  font-weight: 500;
`;

const EventToolName = styled.span`
  font-size: 13px;
  color: #743D98;
  font-weight: 500;
`;

const EventNodeName = styled.span`
  font-size: 13px;
  color: #38a169;
  font-weight: 500;
`;

const RetryBadge = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #c05621;
  background: #feebc8;
  padding: 2px 6px;
  border-radius: 4px;
`;

const RateLimitBadge = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #c53030;
  background: #fed7d7;
  padding: 2px 6px;
  border-radius: 4px;
`;

const FinishReasonBadge = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #c05621;
  background: #feebc8;
  padding: 2px 6px;
  border-radius: 4px;
`;

const EventMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const EventDuration = styled.span`
  font-size: 12px;
  color: #8890A5;
  font-family: monospace;
`;

const EventTokens = styled.span`
  font-size: 12px;
  color: #8890A5;
`;

const ExpandIcon = styled.span`
  font-size: 10px;
  color: #8890A5;
`;

const EventDetails = styled.div`
  padding: 16px;
  border-top: 1px solid #E4E7EF;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ErrorSection = styled.div`
  background: #fff5f5;
  border: 1px solid #feb2b2;
  border-radius: 6px;
  padding: 12px;
`;

const ErrorLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #c53030;
  margin-bottom: 4px;
`;

const ErrorMessage = styled.div`
  font-size: 14px;
  color: #742a2a;
  font-family: monospace;
`;

const TopLevelErrorSection = styled.div`
  margin: 0;
  padding: 16px 32px;
  background: #fff5f5;
  border-bottom: 1px solid #feb2b2;
`;

const TopLevelErrorLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #c53030;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const TopLevelErrorMessage = styled.div`
  font-size: 14px;
  color: #742a2a;
  font-family: monospace;
  white-space: pre-wrap;
  word-break: break-word;
`;

const FinalResponseSection = styled.div`
  margin: 0;
  background: #ecfeff;
  border-bottom: 1px solid #67e8f9;
`;

const FinalResponseHeader = styled.div`
  padding: 12px 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: #cffafe;
  }
`;

const FinalResponseLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #155e75;
  text-transform: uppercase;
`;

const FinalResponseContent = styled.div`
  padding: 0 32px 16px 32px;
  font-size: 14px;
  color: #164e63;
  line-height: 1.6;
  max-height: 400px;
  overflow-y: auto;

  p {
    margin: 0 0 8px 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  pre {
    margin: 0;
    font-size: 12px;
    font-family: monospace;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const ReasoningSection = styled.div`
  background: #fffbeb;
  border: 1px solid #fbd38d;
  border-radius: 6px;
  overflow: hidden;
`;

const ReasoningHeader = styled.div`
  padding: 8px 12px;
  background: #fef3c7;
  font-size: 12px;
  font-weight: 600;
  color: #92400e;
  text-transform: uppercase;
`;

const ReasoningContent = styled.div`
  padding: 12px;
  font-size: 14px;
  color: #78350f;
  line-height: 1.6;
  max-height: 300px;
  overflow-y: auto;

  p {
    margin: 0 0 8px 0;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

const CompletionSection = styled.div`
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 6px;
  overflow: hidden;
`;

const CompletionHeader = styled.div`
  padding: 8px 12px;
  background: #dcfce7;
  font-size: 12px;
  font-weight: 600;
  color: #166534;
  text-transform: uppercase;
`;

const CompletionContent = styled.div`
  padding: 12px;
  font-size: 14px;
  color: #14532d;
  line-height: 1.6;
  max-height: 300px;
  overflow-y: auto;

  p {
    margin: 0 0 8px 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  pre {
    margin: 0;
    font-size: 12px;
    font-family: monospace;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const TokenSection = styled.div`
  background: #FAFAFC;
  border: 1px solid #E4E7EF;
  border-radius: 6px;
  overflow: hidden;
`;

const TokenHeader = styled.div`
  padding: 8px 12px;
  background: #E4E7EF;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
`;

const TokenGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  padding: 12px;
  gap: 16px;
`;

const TokenItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TokenLabel = styled.div`
  font-size: 11px;
  color: #64748b;
`;

const TokenValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #334155;
`;

const ToolSection = styled.div`
  background: #faf5ff;
  border: 1px solid #d8b4fe;
  border-radius: 6px;
  overflow: hidden;
`;

const ToolHeader = styled.div`
  padding: 8px 12px;
  background: #f3e8ff;
  font-size: 12px;
  font-weight: 600;
  color: #6b21a8;
  text-transform: uppercase;
`;

const ToolInputOutput = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
`;

const ToolBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ToolBlockLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #7c3aed;
`;

const ToolBlockContent = styled.div`
  background: white;
  border: 1px solid #e9d5ff;
  border-radius: 4px;
  padding: 8px;
  max-height: 200px;
  overflow-y: auto;

  pre {
    margin: 0;
    font-size: 12px;
    font-family: monospace;
    white-space: pre-wrap;
    word-break: break-word;
    color: #4c1d95;
  }
`;

const NodeSection = styled.div`
  background: #f0fff4;
  border: 1px solid #9ae6b4;
  border-radius: 6px;
  overflow: hidden;
`;

const NodeHeader = styled.div`
  padding: 8px 12px;
  background: #c6f6d5;
  font-size: 12px;
  font-weight: 600;
  color: #22543d;
  text-transform: uppercase;
`;

const NodeType = styled.div`
  padding: 8px 12px;
  font-size: 13px;
  color: #276749;
`;

const ModalFooter = styled.div`
  padding: 16px 32px;
  border-top: 1px solid #E4E7EF;
  display: flex;
  justify-content: flex-end;
`;

export default TraceDetailModal;
